import React from "react";
import "./WebsiteLinkPreview.css";

/**
 * Website Link Preview (External Experience — Web renderer).
 *
 * Renders a responsive, clearly-external clickable card for a link that the
 * backend resolved into OpenGraph metadata. Priority is handled upstream:
 * YouTube/Vimeo keep their existing video embeds and never reach this card.
 *
 * The card consumes the platform-agnostic `preview.destination` descriptor the
 * backend emits, so a future Roku / Fire TV / Android-TV renderer can open the
 * SAME destination without any change to Community Post/Comment code.
 */

const INLINE_URL_RE = /(https?:\/\/[^\s<>"'\)\]]+)/gi;

function trimTrailingPunctuation(url) {
  const match = url.match(/[.,;:!?]+$/);
  if (!match) {
    return { url: url, trailing: "" };
  }
  return {
    url: url.slice(0, url.length - match[0].length),
    trailing: match[0],
  };
}

/**
 * Turn raw http(s) URLs inside a body-text string into real external links so
 * the original URL is always usable — even when no preview could be resolved.
 * Non-URL text is returned unchanged (identical rendered output).
 *
 * @param {string} text
 * @param {string} keyBase
 * @returns {(string|JSX.Element)[]|string}
 */
export function renderTextWithLinks(text, keyBase = "lnk") {
  const raw = String(text == null ? "" : text);
  if (!raw) return raw;

  const out = [];
  const re = new RegExp(INLINE_URL_RE.source, "gi");
  let last = 0;
  let idx = 0;
  let m;
  while ((m = re.exec(raw)) !== null) {
    const start = m.index;
    const whole = m[0];
    const split = trimTrailingPunctuation(whole);
    if (start > last) {
      out.push(raw.slice(last, start));
    }
    out.push(
      <a
        key={keyBase + "-" + idx}
        className="community-post__link"
        href={split.url}
        target="_blank"
        rel="noopener noreferrer"
      >
        {split.url}
      </a>
    );
    if (split.trailing) {
      out.push(split.trailing);
    }
    last = start + whole.length;
    idx++;
  }
  if (last < raw.length) {
    out.push(raw.slice(last));
  }
  return out.length ? out : raw;
}

function hideBrokenMedia(event) {
  const node = event && event.target ? event.target.parentNode : null;
  if (node && node.style) {
    node.style.display = "none";
  }
}

export function WebsiteLinkPreview({ preview }) {
  if (!preview) return null;

  const destination = preview.destination || {};
  const href = destination.url || preview.url || preview.source_url;
  if (!href) return null;

  const rel = destination.rel || "noopener noreferrer";
  const target = destination.target || "_blank";
  const domain = preview.domain || preview.site_name || href;
  const eyebrow = preview.site_name || domain;
  const title = preview.title || domain;

  return (
    <a
      className="community-link-preview"
      href={href}
      target={target}
      rel={rel}
      aria-label={"Opens external site " + domain + " in a new tab"}
    >
      {preview.image ? (
        <span className="community-link-preview__media">
          <img
            src={preview.image}
            alt=""
            loading="lazy"
            referrerPolicy="no-referrer"
            onError={hideBrokenMedia}
          />
        </span>
      ) : null}
      <span className="community-link-preview__body">
        <span className="community-link-preview__site">
          <span className="community-link-preview__domain">{eyebrow}</span>
          <span className="community-link-preview__ext" aria-hidden="true">
            External link
          </span>
        </span>
        <span className="community-link-preview__title">{title}</span>
        {preview.description ? (
          <span className="community-link-preview__desc">
            {preview.description}
          </span>
        ) : null}
        <span className="community-link-preview__url">{domain}</span>
      </span>
    </a>
  );
}

export default WebsiteLinkPreview;
